import { useState } from 'react';
import type { PreviewDto } from '@konvert/shared';
import { formatDateWithWeekdayRu, formatMinutesRu } from '@konvert/domain';
import { Sheet } from './Sheet.js';
import { t } from '../lib/i18n.js';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  proposalId?: string | null;
  preview?: PreviewDto | null;
}

/**
 * The assistant chat (SPEC 11.2, экран 6). A proposal is shown as a form with
 * what would change and what the day would look like — applying is a separate,
 * deliberate tap.
 */
export function AssistantSheet({
  open,
  messages,
  pending,
  onClose,
  onSend,
  onApply,
  onReject,
}: {
  open: boolean;
  messages: ChatMessage[];
  pending: boolean;
  onClose: () => void;
  onSend: (text: string, supersedes?: string) => void;
  onApply: (proposalId: string, force: boolean) => void;
  onReject: (proposalId: string) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [revising, setRevising] = useState<string | null>(null);

  const send = (): void => {
    if (!text.trim()) return;
    onSend(text.trim(), revising ?? undefined);
    setText('');
    setRevising(null);
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('assistant.title')}>
      <div className="space-y-3">
        {messages.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-soft">{t('assistant.empty')}</p>
        ) : null}

        {messages.map((message) => (
          <div key={message.id} className="space-y-2">
            <div
              className={
                message.role === 'user'
                  ? 'ml-auto max-w-[85%] rounded-[var(--radius-card)] bg-[var(--kraft)]/50 px-3 py-2 text-sm text-ink'
                  : 'max-w-[90%] text-sm text-ink'
              }
            >
              {message.text.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>

            {message.preview && message.proposalId ? (
              <ProposalCard
                preview={message.preview}
                onApply={(force) => onApply(message.proposalId!, force)}
                onReject={() => onReject(message.proposalId!)}
                onRevise={() => setRevising(message.proposalId!)}
              />
            ) : null}
          </div>
        ))}

        {pending ? <p className="text-sm text-ink-soft">{t('assistant.thinking')}</p> : null}

        {revising ? (
          <p className="text-xs text-ink-soft">{t('assistant.revise_placeholder')}:</p>
        ) : null}

        <div className="flex gap-2">
          <input
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && send()}
            placeholder={revising ? t('assistant.revise_placeholder') : t('assistant.placeholder')}
            className="min-w-0 flex-1 rounded-[var(--radius-card)] border border-[var(--line)] bg-paper px-3 py-2 text-sm text-ink outline-none"
          />
          <button
            type="button"
            onClick={send}
            disabled={!text.trim() || pending}
            className="rounded-[var(--radius-card)] bg-[var(--ink)] px-4 py-2 text-sm text-[var(--paper)] disabled:opacity-40"
          >
            →
          </button>
        </div>
      </div>
    </Sheet>
  );
}

function ProposalCard({
  preview,
  onApply,
  onReject,
  onRevise,
}: {
  preview: PreviewDto;
  onApply: (force: boolean) => void;
  onReject: () => void;
  onRevise: () => void;
}): JSX.Element {
  const exceeded = preview.affected_days.some((day) => day.load_after.exceeded);

  return (
    <div className="paper-card space-y-2 p-3">
      <ul className="space-y-1 text-sm text-ink">
        {preview.moves.map((move, index) => (
          <li key={index}>
            «{move.title}» → {targetName(move.to)}
          </li>
        ))}
      </ul>

      {preview.affected_days.map((day) => (
        <p key={day.date} className={`text-xs ${day.load_after.exceeded ? 'text-brick' : 'text-ink-soft'}`}>
          {formatDateWithWeekdayRu(day.date)}: {t('assistant.load_after')}{' '}
          {formatMinutesRu(day.load_after.time_load_minutes)} из{' '}
          {formatMinutesRu(day.load_after.max_minutes)}, энергия {day.load_after.energy_points} из{' '}
          {day.load_after.max_energy_points}
          {day.conflicts_after.length > 0 ? ' · есть пересечение встреч' : ''}
        </p>
      ))}

      {preview.warnings.map((warning, index) => (
        <p key={index} className="text-xs text-ink-soft">
          {warning.message}
        </p>
      ))}

      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={() => onApply(exceeded)}
          className="rounded-[var(--radius-card)] bg-[var(--ink)] px-3 py-2 text-sm text-[var(--paper)]"
        >
          {exceeded ? t('assistant.force') : t('assistant.apply')}
        </button>
        <button
          type="button"
          onClick={onRevise}
          className="rounded-[var(--radius-card)] border border-[var(--line)] px-3 py-2 text-sm text-ink"
        >
          {t('assistant.revise')}
        </button>
        <button
          type="button"
          onClick={onReject}
          className="rounded-[var(--radius-card)] border border-[var(--line)] px-3 py-2 text-sm text-ink-soft"
        >
          {t('assistant.reject')}
        </button>
      </div>
    </div>
  );
}

function targetName(to: PreviewDto['moves'][number]['to']): string {
  if (to.period_start) return formatDateWithWeekdayRu(to.period_start);
  const names: Record<string, string> = {
    inbox: 'Бэклог',
    someday: 'Someday',
    projects: 'Проекты',
    done: 'Копилка',
    trash: 'Корзина',
    notes_archive: 'Архив заметок',
    carryover: 'Разбор',
  };
  return names[to.kind] ?? to.kind;
}
